import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import './index.css'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import OnboardingSourcesPage from './pages/OnboardingSourcesPage'
import OnboardingDestinationsPage from './pages/OnboardingDestinationsPage'
import OnboardingImportPage from './pages/OnboardingImportPage'
import OnboardingSuccessPage from './pages/OnboardingSuccessPage'
import DashboardPage from './pages/DashboardPage'
import EditorPage from './pages/EditorPage'
import SkillCreatorPage from './pages/SkillCreatorPage'
import SettingsPage from './pages/SettingsPage'
import OnboardingPage from './pages/OnboardingPage'
import InvitePage from './pages/InvitePage'
import AnalyticsPage from './pages/AnalyticsPage'
import AuthCallbackPage from './pages/AuthCallbackPage'
import ConnectPage from './pages/ConnectPage'
import JoinPage from './pages/JoinPage'
import CookieConsent from './components/CookieConsent'
import PrivacyPage from './pages/PrivacyPage'
import InvitationAcceptPage from './pages/InvitationAcceptPage'
import TemplatesPage from './pages/TemplatesPage'
import TemplateBuilderPage from './pages/TemplateBuilderPage'
import OrgTemplatesPage from './pages/OrgTemplatesPage'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  if (loading) return null
  if (!session) return <Navigate to="/login" replace />
  return <>{children}</>
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { session, loading } = useAuth()
  if (loading) return null
  if (session) {
    const params = new URLSearchParams(window.location.search)
    const redirect = params.get('redirect')
    return <Navigate to={redirect || '/'} replace />
  }
  return <>{children}</>
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
      <Route path="/onboarding/sources" element={<PrivateRoute><OnboardingSourcesPage /></PrivateRoute>} />
      <Route path="/onboarding/destinations" element={<PrivateRoute><OnboardingDestinationsPage /></PrivateRoute>} />
      <Route path="/onboarding/import" element={<PrivateRoute><OnboardingImportPage /></PrivateRoute>} />
      <Route path="/onboarding/success" element={<PrivateRoute><OnboardingSuccessPage /></PrivateRoute>} />
      <Route path="/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />
      <Route path="/" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
      <Route path="/connect" element={<PrivateRoute><ConnectPage /></PrivateRoute>} />
      <Route path="/analytics" element={<PrivateRoute><AnalyticsPage /></PrivateRoute>} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/templates/org" element={<PrivateRoute><OrgTemplatesPage /></PrivateRoute>} />
      <Route path="/templates/:name/build" element={<PrivateRoute><TemplateBuilderPage /></PrivateRoute>} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth/confirm" element={<AuthCallbackPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/invitations/accept" element={<InvitationAcceptPage />} />
      <Route path="/join/:slug" element={<JoinPage />} />
      <Route path="/skills/new" element={<PrivateRoute><SkillCreatorPage /></PrivateRoute>} />
      <Route path="/skills/:name" element={<PrivateRoute><EditorPage /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter basename="/">
      <AuthProvider>
        <AppRoutes />
        <CookieConsent />
      </AuthProvider>
    </BrowserRouter>
  )
}
